import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import { collection, addDoc } from "firebase/firestore";
import { firestore } from "../firebase"; // Adjust path to your Firebase config

const DesignHelpPopover = ({ canvas, open, onClose }) => {
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [formState, setFormState] = useState(false); // Boolean: false (form), true (success)
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    signtype: "",
    size: "",
    thickness: "",
    numbersides: "",
    signtext: "",
    signcolor: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  // Validation functions
  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone) => {
    return /^\+?[\d\s-]{7,}$/.test(phone); // Basic phone validation: at least 7 digits, allows +, -, spaces
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: false })); // Clear error on input change
  };

  // Validate and submit form
  const handleDesignRequest = async () => {
    setIsSendingEmail(true);

    // Validate required fields
    const newErrors = {
      firstName: formData.firstName.length < 2,
      lastName: formData.lastName.length < 2,
      email: !validateEmail(formData.email),
      phone: !validatePhone(formData.phone),
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      setIsSendingEmail(false);
      return;
    }

    // Generate email content
    const textMessage = `
      <p><strong>First Name:</strong> ${formData.firstName}</p>
      <p><strong>Last Name:</strong> ${formData.lastName}</p>
      <p><strong>Email:</strong> ${formData.email}</p>
      <p><strong>Phone:</strong> ${formData.phone}</p>
      <p><strong>Sign Type:</strong> ${formData.signtype || "Not specified"}</p>
      <p><strong>Size:</strong> ${formData.size || "Not specified"}</p>
      <p><strong>Thickness:</strong> ${
        formData.thickness || "Not specified"
      }</p>
      <p><strong>Sides:</strong> ${formData.numbersides || "Not specified"}</p>
      <p><strong>Sign Text:</strong> ${formData.signtext || "Not specified"}</p>
      <p><strong>Sign Color:</strong> ${
        formData.signcolor || "Not specified"
      }</p>
      <p><strong>Comments:</strong> ${formData.message || "None"}</p>
    `;

    // Generate canvas screenshot
    const dataURL = canvas.toDataURL({ format: "jpeg" });

    // Prepare Firestore data
    const data = {
      to: ["info@alphabetsigns.com", formData.email],
      createdAt: new Date().toISOString(),
      message: {
        subject: "Sign Design Request",
        text: textMessage,
        html: textMessage,
        attachments: [{ path: dataURL }],
      },
    };

    try {
      const ref = collection(firestore, "sendEmailRequests");
      await addDoc(ref, data);
      setFormState(true);
    } catch (e) {
      console.error("DesignHelpPopover: Error sending email request", e);
      alert("Failed to send design request. Please try again.");
    }

    setIsSendingEmail(false);
  };

  // Reset form for another request
  const handleResetForm = () => {
    setFormState(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      signtype: "",
      size: "",
      thickness: "",
      numbersides: "",
      signtext: "",
      signcolor: "",
      message: "",
    });
    setErrors({
      firstName: false,
      lastName: false,
      email: false,
      phone: false,
    });
  };

  return (
    <Dialog
      disableScrollLock
      open={open}
      onClose={onClose}
      TransitionComponent={Zoom}
      fullWidth
      maxWidth="sm"
      aria-labelledby="design-help-dialog-title"
      aria-describedby="design-help-dialog-description"
    >
      <DialogTitle
        id="design-help-dialog-title"
        sx={{
          backgroundColor: "primary.main",
          color: "white",
          fontFamily: "Roboto",
          fontWeight: 500,
        }}
      >
        Get Design Help
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText id="design-help-dialog-description" sx={{ p: 3 }}>
          {!formState ? (
            <FormControl component="fieldset" fullWidth>
              <FormLabel sx={{ mb: 1, color: "text.primary" }}>
                Contact Information
              </FormLabel>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  id="firstName"
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  error={errors.firstName}
                  helperText={
                    errors.firstName ? "Name is required (min 2 chars)" : ""
                  }
                  required
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  id="lastName"
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  error={errors.lastName}
                  helperText={
                    errors.lastName ? "Name is required (min 2 chars)" : ""
                  }
                  required
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Stack>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  id="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  error={errors.email}
                  helperText={errors.email ? "Valid email is required" : ""}
                  required
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  id="phone"
                  label="Phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  error={errors.phone}
                  helperText={
                    errors.phone ? "Valid phone number is required" : ""
                  }
                  required
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Stack>
              <FormLabel sx={{ mb: 1, color: "text.primary" }}>
                Design Information
              </FormLabel>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
                  <TextField
                    id="signtype"
                    label="Sign Type"
                    value={formData.signtype}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                  <TextField
                    id="size"
                    label="Size"
                    value={formData.size}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                  <TextField
                    id="thickness"
                    label="Thickness"
                    value={formData.thickness}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                  <TextField
                    id="numbersides"
                    label="Sides"
                    value={formData.numbersides}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                </Stack>
                <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
                  <TextField
                    id="signtext"
                    label="Sign Text"
                    value={formData.signtext}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                  <TextField
                    id="signcolor"
                    label="Sign Colors"
                    value={formData.signcolor}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                  <TextField
                    id="message"
                    label="Comments"
                    value={formData.message}
                    onChange={handleInputChange}
                    multiline
                    minRows={3}
                    maxRows={5}
                    variant="outlined"
                    fullWidth
                  />
                </Stack>
              </Stack>
              <Typography
                variant="body2"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                Your design will be emailed to our team as a JPEG attachment. A
                professional designer will contact you within one business day.
              </Typography>
              <Button
                variant="contained"
                onClick={handleDesignRequest}
                disabled={isSendingEmail}
                sx={{
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                }}
              >
                {isSendingEmail ? "Sending..." : "Request Design Help"}
              </Button>
            </FormControl>
          ) : (
            <Box>
              <Typography
                variant="h6"
                sx={{ color: "success.main", mb: 2, fontFamily: "Roboto" }}
              >
                Success
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, color: "text.primary" }}>
                Your design request has been sent to our team. A professional
                designer will contact you within one business day.
              </Typography>
              <Button
                variant="contained"
                onClick={handleResetForm}
                sx={{
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                }}
              >
                Send Another Request
              </Button>
            </Box>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end", p: 2 }}>
        <Button variant="text" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DesignHelpPopover;
